import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { useDispatch } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import Button from 'components/Button';
import FullPageError from 'components/FullPageError';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import AppContextProvider from 'context/AppContext';
import { SelectedSetProvider } from 'context/SelectedSet';
import useAuthenticationInterceptor from 'hooks/useAuthenticationInterceptor';
import AppRoutes from 'routing/AppRoutes';
import { AppDispatch } from 'store';
import { api } from 'store/api/api';
import { Auth0Error } from 'store/user/auth0.helpers';
import { setAccessToken } from 'store/user/userSlice';

const App: React.FC = () => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();

  useAuthenticationInterceptor();

  const auth0Error = error as unknown as Auth0Error;
  const passwordResetRequired =
    auth0Error?.error === 'access_denied' && auth0Error?.error_description.toLowerCase().includes('password');

  useEffect(() => {
    if (isAuthenticated && !error) {
      getAccessTokenSilently()
        .then(token => {
          dispatch(setAccessToken(token));
        })
        .catch(e => {
          console.warn(e);
          dispatch(setAccessToken(null));
          dispatch(api.util.resetApiState());
        });
    }
  }, [getAccessTokenSilently, dispatch, loginWithRedirect, error, isAuthenticated]);

  if (isLoading) {
    return <PageSpinner />;
  } else if (passwordResetRequired) {
    return (
      <FullPageError>
        <p>Your password has expired and needs to be reset before you can log in.</p>
        <p>Please follow the following steps:</p>
        <ol>
          <li>Click the Reset Password button below</li>
          <li>If prompted, enter your organization on the next screen</li>
          <li>
            On the username and password page, click the <strong>Forgot Password button</strong>
          </li>
          <li>Follow the instructions in the email you receive</li>
          <li>Return to the app and login as usual, with your new password</li>
        </ol>
        <br />
        <Button
          onClick={() => {
            loginWithRedirect({
              prompt: 'login', // important to avoid endless redirect loop
            });
          }}
        >
          Reset Password
        </Button>
      </FullPageError>
    );
  } else if (error) {
    const _error = error as unknown as Auth0Error;
    return (
      <FullPageError>
        <p>{_error.error_description}</p>
        <Button onClick={loginWithRedirect}>Retry</Button>
      </FullPageError>
    );
  }
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <FullPageError>
          <p>{JSON.stringify(error)}</p>
          <p>Try reloading the webpage</p>
        </FullPageError>
      )}
    >
      <AppContextProvider>
        <SelectedSetProvider>
          <AppRoutes />
        </SelectedSetProvider>
      </AppContextProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
